import { useAuth, useUser } from "@clerk/clerk-react";
import DOMPurify from "dompurify";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  ArrowRight,
  BookOpen,
  Calendar,
  Check,
  ChevronLeft,
  Clock,
  Download,
  Edit,
  File,
  FileText,
  FileUp,
  Menu,
  Paperclip,
  Plus,
  Save,
  Search,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import Navbar from "../components/Navbar";
import "./NotesAurora.css";

const url = process.env.REACT_APP_BACK_END_URL;

const EMPTY_NOTE = {
  _id: null,
  title: "",
  content: "",
  attachments: [],
};

const EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    ["link"],
    [{ align: [] }],
    ["clean"],
  ],
  clipboard: { matchVisual: false },
};

const EDITOR_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "blockquote",
  "code-block",
  "link",
  "align",
];

const formatDate = (value, options = {}) => {
  if (!value) return "Recently";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Recently";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...options,
  }).format(date);
};

const stripRichText = (html = "") => {
  const clean = DOMPurify.sanitize(html);
  if (typeof document === "undefined") {
    return clean.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  const container = document.createElement("div");
  container.innerHTML = clean;
  return (container.textContent || "").replace(/\s+/g, " ").trim();
};

const getFileType = (filename = "") => {
  const extension = filename.split(".").pop()?.toLowerCase();
  if (extension === "pdf") return "pdf";
  if (["doc", "docx"].includes(extension)) return "document";
  if (["xls", "xlsx"].includes(extension)) return "sheet";
  if (["png", "jpg", "jpeg", "gif"].includes(extension)) return "image";
  return "file";
};

const FileTypeIcon = ({ filename }) => {
  const type = getFileType(filename);
  return (
    <span className={`an-file-icon is-${type}`} aria-hidden="true">
      {type === "file" ? <File size={18} /> : <FileText size={18} />}
    </span>
  );
};

const IconButton = ({ label, children, className = "", ...props }) => (
  <button
    type="button"
    className={`an-icon-button ${className}`}
    aria-label={label}
    title={label}
    {...props}
  >
    {children}
  </button>
);

const ActionButton = ({
  children,
  icon,
  variant = "primary",
  className = "",
  loading = false,
  disabled = false,
  ...props
}) => (
  <button
    type="button"
    className={`an-button is-${variant} ${className}`}
    disabled={loading || disabled}
    {...props}
  >
    {loading ? <span className="an-button-spinner" aria-hidden="true" /> : icon}
    <span>{children}</span>
  </button>
);

const LoadingState = ({ compact = false }) => (
  <div className={`an-loading ${compact ? "is-compact" : ""}`} role="status">
    <span className="an-loading-mark" aria-hidden="true" />
    <span>Loading your notes</span>
  </div>
);

const NotesAurora = () => {
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState("default");
  const [currentNote, setCurrentNote] = useState(EMPTY_NOTE);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [newPageTitle, setNewPageTitle] = useState("");
  const [isAddingPage, setIsAddingPage] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => (
    typeof window === "undefined" ? true : window.innerWidth >= 980
  ));
  const [editingPageId, setEditingPageId] = useState(null);
  const [editingPageTitle, setEditingPageTitle] = useState("");
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const fileInputRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const { user } = useUser();
  const { getToken } = useAuth();

  const notify = useCallback((message, type = "success") => {
    window.clearTimeout(noticeTimerRef.current);
    setNotice({ message, type });
    noticeTimerRef.current = window.setTimeout(() => setNotice(null), 3600);
  }, []);

  useEffect(() => () => window.clearTimeout(noticeTimerRef.current), []);

  const fetchPages = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/pages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Unable to load notebooks");

      const data = await response.json();
      const sortedPages = data
        .map((page) => ({ ...page, notes: page.notes || [] }))
        .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));

      setPages(sortedPages);
      setCurrentPage((previousPage) => {
        if (sortedPages.some((page) => page._id === previousPage)) return previousPage;
        return sortedPages[0]?._id || "default";
      });
    } catch (error) {
      console.error("Error fetching notebooks:", error);
      notify("We couldn't load your notebooks. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [getToken, notify]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      if (expandedNoteId) setExpandedNoteId(null);
      else if (isEditing) {
        setIsEditing(false);
        setCurrentNote(EMPTY_NOTE);
        setAttachments([]);
      } else if (window.innerWidth < 980 && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [expandedNoteId, isEditing, isSidebarOpen]);

  useEffect(() => {
    const shouldLock = expandedNoteId || (isSidebarOpen && window.innerWidth < 980);
    if (!shouldLock) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [expandedNoteId, isSidebarOpen]);

  const currentPageData = useMemo(
    () => pages.find((page) => page._id === currentPage) || null,
    [pages, currentPage]
  );

  const sortedNotes = useMemo(() => (
    [...(currentPageData?.notes || [])].sort(
      (a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
    )
  ), [currentPageData]);

  const filteredNotes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return sortedNotes;
    return sortedNotes.filter((note) => (
      note.title?.toLowerCase().includes(query)
      || stripRichText(note.content).toLowerCase().includes(query)
    ));
  }, [searchQuery, sortedNotes]);

  const expandedNote = useMemo(
    () => sortedNotes.find((note) => note._id === expandedNoteId) || null,
    [expandedNoteId, sortedNotes]
  );

  const totalNotes = useMemo(
    () => pages.reduce((total, page) => total + (page.notes?.length || 0), 0),
    [pages]
  );

  const attachmentCount = useMemo(
    () => sortedNotes.reduce((total, note) => total + (note.attachments?.length || 0), 0),
    [sortedNotes]
  );

  const resetEditor = () => {
    setCurrentNote(EMPTY_NOTE);
    setAttachments([]);
    setIsEditing(false);
  };

  const selectPage = (pageId) => {
    setCurrentPage(pageId);
    setSearchQuery("");
    setExpandedNoteId(null);
    resetEditor();
    if (window.innerWidth < 980) setIsSidebarOpen(false);
  };

  const startNewNote = () => {
    if (!currentPageData) return;
    setCurrentNote(EMPTY_NOTE);
    setAttachments([]);
    setExpandedNoteId(null);
    setIsEditing(true);
  };

  const startEditingNote = (note) => {
    setCurrentNote({ ...note, attachments: note.attachments || [] });
    setAttachments([]);
    setExpandedNoteId(null);
    setIsEditing(true);
  };

  const handleAddPage = async (event) => {
    event?.preventDefault();
    const title = newPageTitle.trim();
    if (!title) return;

    setIsSaving(true);
    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/pages`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ title }),
      });
      if (!response.ok) throw new Error("Unable to create notebook");

      const page = await response.json();
      const newPage = { ...page, notes: page.notes || [] };
      setPages((previousPages) => [newPage, ...previousPages]);
      setCurrentPage(newPage._id);
      setNewPageTitle("");
      setIsAddingPage(false);
      if (window.innerWidth < 980) setIsSidebarOpen(false);
      notify(`“${title}” is ready for your notes.`);
    } catch (error) {
      console.error("Error creating notebook:", error);
      notify("We couldn't create that notebook.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const beginRenamingPage = (page) => {
    setEditingPageId(page._id);
    setEditingPageTitle(page.title);
  };

  const handleRenamePage = async (event, pageId) => {
    event?.preventDefault();
    const title = editingPageTitle.trim();
    if (!title) return;

    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/pages/${pageId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ title }),
      });
      if (!response.ok) throw new Error("Unable to rename notebook");
      const updatedPage = await response.json();
      setPages((previousPages) => previousPages.map((page) => (
        page._id === pageId ? { ...page, ...updatedPage, notes: page.notes || [] } : page
      )));
      setEditingPageId(null);
      notify("Notebook renamed.");
    } catch (error) {
      console.error("Error renaming notebook:", error);
      notify("We couldn't rename that notebook.", "error");
    }
  };

  const handleDeletePage = async (page) => {
    const confirmed = window.confirm(
      `Delete “${page.title}” and all ${page.notes?.length || 0} notes inside it? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/pages/${page._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Unable to delete notebook");

      setPages((previousPages) => {
        const nextPages = previousPages.filter((item) => item._id !== page._id);
        setCurrentPage((selectedPage) => (
          selectedPage === page._id ? nextPages[0]?._id || "default" : selectedPage
        ));
        return nextPages;
      });
      resetEditor();
      notify("Notebook deleted.");
    } catch (error) {
      console.error("Error deleting notebook:", error);
      notify("We couldn't delete that notebook.", "error");
    }
  };

  const validateFiles = (files) => {
    const allowed = ["pdf", "doc", "docx", "xls", "xlsx"];
    return files.filter((file) => {
      const extension = file.name.split(".").pop()?.toLowerCase();
      if (!allowed.includes(extension)) {
        notify(`${file.name} isn't a supported file type.`, "error");
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        notify(`${file.name} is larger than 10 MB.`, "error");
        return false;
      }
      return true;
    });
  };

  const addFiles = (files) => {
    const validFiles = validateFiles(Array.from(files));
    if (!validFiles.length) return;
    setAttachments((previousFiles) => {
      const availableSlots = Math.max(0, 5 - previousFiles.length);
      if (validFiles.length > availableSlots) {
        notify("You can add up to five files at a time.", "error");
      }
      return [...previousFiles, ...validFiles.slice(0, availableSlots)];
    });
  };

  const handleFileUpload = (event) => {
    addFiles(event.target.files);
    event.target.value = "";
  };

  const persistNote = async () => {
    const title = currentNote.title.trim();
    if (!title || !stripRichText(currentNote.content)) {
      notify("Add a title and some note content before saving.", "error");
      return;
    }

    const isUpdating = Boolean(currentNote._id);
    setIsSaving(true);
    try {
      const token = await getToken();
      const formData = new FormData();
      if (!isUpdating) formData.append("pageId", currentPage);
      formData.append("title", title);
      formData.append("content", currentNote.content);
      formData.append("format", "html");
      attachments.forEach((file) => formData.append("attachments", file));

      const response = await fetch(
        isUpdating ? `${url}/api/notes/${currentNote._id}` : `${url}/api/notes`,
        {
          method: isUpdating ? "PUT" : "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );
      if (!response.ok) throw new Error("Unable to save note");
      const savedNote = await response.json();

      setPages((previousPages) => previousPages.map((page) => {
        if (page._id !== currentPage) return page;
        const notes = isUpdating
          ? (page.notes || []).map((note) => note._id === savedNote._id ? savedNote : note)
          : [savedNote, ...(page.notes || [])];
        return { ...page, notes, updatedAt: savedNote.updatedAt || new Date().toISOString() };
      }));
      resetEditor();
      notify(isUpdating ? "Note updated." : "Note saved to your notebook.");
    } catch (error) {
      console.error("Error saving note:", error);
      notify("We couldn't save that note. Your draft is still here.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNote = async (note) => {
    if (!window.confirm(`Delete “${note.title}”? This cannot be undone.`)) return;
    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/notes/${note._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Unable to delete note");
      setPages((previousPages) => previousPages.map((page) => (
        page._id === currentPage
          ? { ...page, notes: (page.notes || []).filter((item) => item._id !== note._id) }
          : page
      )));
      if (expandedNoteId === note._id) setExpandedNoteId(null);
      notify("Note deleted.");
    } catch (error) {
      console.error("Error deleting note:", error);
      notify("We couldn't delete that note.", "error");
    }
  };

  const handleDeleteAttachment = async (noteId, file) => {
    if (!window.confirm(`Remove ${file.originalName}?`)) return;
    try {
      const token = await getToken();
      const response = await fetch(
        `${url}/api/notes/${noteId}/attachments/${encodeURIComponent(file.filename)}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error("Unable to delete attachment");

      const withoutFile = (list = []) => list.filter((item) => item.filename !== file.filename);
      setPages((previousPages) => previousPages.map((page) => (
        page._id === currentPage
          ? {
              ...page,
              notes: (page.notes || []).map((note) => note._id === noteId
                ? { ...note, attachments: withoutFile(note.attachments) }
                : note),
            }
          : page
      )));
      setCurrentNote((note) => note._id === noteId
        ? { ...note, attachments: withoutFile(note.attachments) }
        : note);
      notify("Attachment removed.");
    } catch (error) {
      console.error("Error deleting attachment:", error);
      notify("We couldn't remove that attachment.", "error");
    }
  };

  const handleDownloadAttachment = async (noteId, file) => {
    try {
      const token = await getToken();
      const response = await fetch(
        `${url}/api/notes/${noteId}/attachments/${encodeURIComponent(file.filename)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("Unable to download attachment");
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = file.originalName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading attachment:", error);
      notify("We couldn't download that attachment.", "error");
    }
  };

  const renderAttachment = (file, noteId, allowDelete = false) => (
    <div className="an-attachment-row" key={file.filename || file.name}>
      <FileTypeIcon filename={file.originalName || file.name} />
      <div className="an-attachment-copy">
        <strong>{file.originalName || file.name}</strong>
        <span>{file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : "Attachment"}</span>
      </div>
      {noteId && (
        <IconButton label={`Download ${file.originalName}`} onClick={() => handleDownloadAttachment(noteId, file)}>
          <Download size={17} />
        </IconButton>
      )}
      {allowDelete && noteId && (
        <IconButton
          label={`Delete ${file.originalName}`}
          className="is-danger"
          onClick={() => handleDeleteAttachment(noteId, file)}
        >
          <Trash2 size={17} />
        </IconButton>
      )}
    </div>
  );

  const lastUpdated = sortedNotes[0]?.updatedAt || sortedNotes[0]?.createdAt || currentPageData?.updatedAt;

  return (
    <div className="aurora-notes-app">
      <Navbar />

      {notice && (
        <div className={`an-notice is-${notice.type}`} role="status" aria-live="polite">
          {notice.type === "success" ? <Check size={17} /> : <Sparkles size={17} />}
          <span>{notice.message}</span>
          <IconButton label="Dismiss message" onClick={() => setNotice(null)}>
            <X size={15} />
          </IconButton>
        </div>
      )}

      <div className={`an-shell ${isSidebarOpen ? "is-sidebar-open" : "is-sidebar-closed"}`}>
        <button
          type="button"
          className="an-sidebar-backdrop"
          aria-label="Close notebooks panel"
          onClick={() => setIsSidebarOpen(false)}
        />

        <aside className="an-sidebar" aria-label="Notebooks">
          <div className="an-sidebar-header">
            <div className="an-sidebar-brand">
              <span className="an-sidebar-brand-mark"><BookOpen size={19} /></span>
              <div>
                <strong>Noted</strong>
                <span>Preparation library</span>
              </div>
            </div>
            <IconButton label="Collapse notebooks" onClick={() => setIsSidebarOpen(false)}>
              <ChevronLeft size={19} />
            </IconButton>
          </div>

          <ActionButton
            variant="primary"
            className="an-new-notebook-button"
            icon={<Plus size={18} />}
            onClick={() => setIsAddingPage(true)}
          >
            New notebook
          </ActionButton>

          {isAddingPage && (
            <form className="an-new-notebook-form" onSubmit={handleAddPage}>
              <label htmlFor="new-notebook-title">Notebook name</label>
              <input
                id="new-notebook-title"
                value={newPageTitle}
                onChange={(event) => setNewPageTitle(event.target.value)}
                placeholder="e.g. Case feedback"
                autoFocus
              />
              <div>
                <ActionButton type="submit" loading={isSaving}>Create</ActionButton>
                <ActionButton
                  variant="ghost"
                  onClick={() => {
                    setIsAddingPage(false);
                    setNewPageTitle("");
                  }}
                >
                  Cancel
                </ActionButton>
              </div>
            </form>
          )}

          <div className="an-sidebar-section-title">
            <span>Your notebooks</span>
            <span>{pages.length}</span>
          </div>

          <div className="an-notebook-list">
            {isLoading && pages.length === 0 ? (
              <LoadingState compact />
            ) : pages.length === 0 ? (
              <div className="an-sidebar-empty">
                <BookOpen size={20} />
                <p>Create a notebook for case feedback, frameworks, math shortcuts, or recruiting notes.</p>
              </div>
            ) : pages.map((page, index) => (
              <div
                className={`an-notebook-row ${currentPage === page._id ? "is-active" : ""}`}
                key={page._id}
                style={{ "--an-index": index }}
              >
                {editingPageId === page._id ? (
                  <form className="an-rename-form" onSubmit={(event) => handleRenamePage(event, page._id)}>
                    <input
                      value={editingPageTitle}
                      onChange={(event) => setEditingPageTitle(event.target.value)}
                      autoFocus
                    />
                    <IconButton label="Save notebook name" type="submit"><Check size={15} /></IconButton>
                    <IconButton label="Cancel rename" onClick={() => setEditingPageId(null)}><X size={15} /></IconButton>
                  </form>
                ) : (
                  <>
                    <button type="button" className="an-notebook-select" onClick={() => selectPage(page._id)}>
                      <span className="an-notebook-initial">{page.title?.charAt(0).toUpperCase() || "N"}</span>
                      <span className="an-notebook-copy">
                        <strong>{page.title}</strong>
                        <span>{page.notes?.length || 0} {page.notes?.length === 1 ? "note" : "notes"}</span>
                      </span>
                    </button>
                    <div className="an-notebook-actions">
                      <IconButton label={`Rename ${page.title}`} onClick={() => beginRenamingPage(page)}>
                        <Edit size={15} />
                      </IconButton>
                      <IconButton label={`Delete ${page.title}`} className="is-danger" onClick={() => handleDeletePage(page)}>
                        <Trash2 size={15} />
                      </IconButton>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="an-sidebar-footer">
            <div className="an-user-avatar">
              {user?.imageUrl ? <img src={user.imageUrl} alt="" /> : <span>{user?.firstName?.charAt(0) || "A"}</span>}
            </div>
            <div className="an-user-copy">
              <strong>{user?.fullName || "Aurora member"}</strong>
              <span>{totalNotes} notes across {pages.length} notebooks</span>
            </div>
          </div>
        </aside>

        <main className="an-main">
          {!isSidebarOpen && (
            <IconButton label="Open notebooks" className="an-sidebar-opener" onClick={() => setIsSidebarOpen(true)}>
              <Menu size={20} />
            </IconButton>
          )}

          {isLoading && !currentPageData ? (
            <LoadingState />
          ) : !currentPageData ? (
            <section className="an-welcome">
              <div className="an-welcome-copy">
                <span className="an-kicker">Your preparation library</span>
                <h1>Turn every insight into an advantage.</h1>
                <p>
                  Keep case feedback, frameworks, math shortcuts, interview stories, and recruiting research in one focused workspace.
                </p>
                <ActionButton
                  icon={<Plus size={18} />}
                  onClick={() => {
                    setIsSidebarOpen(true);
                    setIsAddingPage(true);
                  }}
                >
                  Create your first notebook
                </ActionButton>
              </div>
              <div className="an-welcome-stack" aria-hidden="true">
                <div className="an-stack-card is-back"><span>03</span><strong>Recruiting research</strong></div>
                <div className="an-stack-card is-middle"><span>02</span><strong>Framework library</strong></div>
                <div className="an-stack-card is-front"><span>01</span><strong>Case feedback</strong><i /></div>
              </div>
            </section>
          ) : (
            <div className="an-workspace">
              <header className="an-workspace-header">
                <div className="an-workspace-title">
                  <span className="an-kicker">Notes workspace</span>
                  <h1>{currentPageData.title}</h1>
                  <p>
                    <Calendar size={14} /> Created {formatDate(currentPageData.createdAt)}
                    <span aria-hidden="true">•</span>
                    <Clock size={14} /> {lastUpdated ? `Updated ${formatDate(lastUpdated)}` : "Ready for your first note"}
                  </p>
                </div>
                {!isEditing && (
                  <div className="an-workspace-actions">
                    <label className="an-search-field">
                      <Search size={18} />
                      <span className="sr-only">Search this notebook</span>
                      <input
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search this notebook"
                      />
                      {searchQuery && (
                        <IconButton label="Clear search" onClick={() => setSearchQuery("")}>
                          <X size={16} />
                        </IconButton>
                      )}
                    </label>
                    <ActionButton icon={<Plus size={18} />} onClick={startNewNote}>New note</ActionButton>
                  </div>
                )}
              </header>

              {!isEditing && (
                <section className="an-metrics" aria-label="Notebook summary">
                  <div><span>Notes</span><strong>{sortedNotes.length.toString().padStart(2, "0")}</strong></div>
                  <div><span>Attachments</span><strong>{attachmentCount.toString().padStart(2, "0")}</strong></div>
                  <div><span>Last activity</span><strong>{lastUpdated ? formatDate(lastUpdated, { year: undefined }) : "New"}</strong></div>
                </section>
              )}

              {isEditing ? (
                <section className="an-editor-sheet">
                  <div className="an-editor-heading">
                    <div>
                      <span className="an-kicker">{currentNote._id ? "Editing note" : "New note"}</span>
                      <h2>{currentNote._id ? "Refine your thinking." : "Capture the useful part."}</h2>
                    </div>
                    <IconButton label="Close editor" onClick={resetEditor}><X size={20} /></IconButton>
                  </div>

                  <div className="an-editor-layout">
                    <div className="an-editor-canvas">
                      <label className="an-title-field">
                        <span>Note title</span>
                        <input
                          value={currentNote.title}
                          onChange={(event) => setCurrentNote((note) => ({ ...note, title: event.target.value }))}
                          placeholder="Give this note a clear title"
                          autoFocus
                        />
                      </label>
                      <div className="an-quill-wrap">
                        <ReactQuill
                          value={currentNote.content}
                          onChange={(content) => setCurrentNote((note) => ({ ...note, content }))}
                          modules={EDITOR_MODULES}
                          formats={EDITOR_FORMATS}
                          placeholder="Write the insight, feedback, framework, or next action you want to remember…"
                          theme="snow"
                        />
                      </div>
                    </div>

                    <aside className="an-attachment-panel">
                      <div className="an-attachment-panel-title">
                        <div>
                          <Paperclip size={17} />
                          <strong>Attachments</strong>
                        </div>
                        <span>{(currentNote.attachments?.length || 0) + attachments.length}</span>
                      </div>

                      {currentNote.attachments?.map((file) => renderAttachment(file, currentNote._id, true))}
                      {attachments.map((file, index) => (
                        <div className="an-attachment-row is-new" key={`${file.name}-${file.lastModified}`}>
                          <FileTypeIcon filename={file.name} />
                          <div className="an-attachment-copy">
                            <strong>{file.name}</strong>
                            <span>{(file.size / 1024 / 1024).toFixed(2)} MB · Ready to upload</span>
                          </div>
                          <IconButton
                            label={`Remove ${file.name}`}
                            className="is-danger"
                            onClick={() => setAttachments((files) => files.filter((_, itemIndex) => itemIndex !== index))}
                          >
                            <X size={16} />
                          </IconButton>
                        </div>
                      ))}

                      <input
                        ref={fileInputRef}
                        type="file"
                        className="an-file-input"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx"
                        onChange={handleFileUpload}
                      />
                      <button
                        type="button"
                        className="an-upload-zone"
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => {
                          event.preventDefault();
                          addFiles(event.dataTransfer.files);
                        }}
                      >
                        <FileUp size={22} />
                        <strong>Add supporting files</strong>
                        <span>PDF, Word, or Excel · 10 MB max · 5 files</span>
                      </button>
                    </aside>
                  </div>

                  <div className="an-editor-actions">
                    <span>Your draft stays open if saving fails.</span>
                    <div>
                      <ActionButton variant="secondary" onClick={resetEditor}>Cancel</ActionButton>
                      <ActionButton icon={<Save size={18} />} onClick={persistNote} loading={isSaving}>
                        {currentNote._id ? "Update note" : "Save note"}
                      </ActionButton>
                    </div>
                  </div>
                </section>
              ) : filteredNotes.length === 0 ? (
                searchQuery ? (
                  <section className="an-search-empty">
                    <Search size={28} />
                    <h2>No notes match “{searchQuery}”.</h2>
                    <p>Try a shorter phrase or clear the search to see the full notebook.</p>
                    <ActionButton variant="secondary" onClick={() => setSearchQuery("")}>Clear search</ActionButton>
                  </section>
                ) : (
                  <section className="an-notes-empty">
                    <div className="an-empty-number">01</div>
                    <div>
                      <span className="an-kicker">Start with what matters</span>
                      <h2>Your first useful note starts here.</h2>
                      <p>Capture feedback while it is fresh, save a framework you want to reuse, or write the next action for your recruiting plan.</p>
                      <ActionButton icon={<Plus size={18} />} onClick={startNewNote}>Create a note</ActionButton>
                    </div>
                    <div className="an-empty-prompts">
                      <span><i>01</i> Case feedback</span>
                      <span><i>02</i> Math shortcuts</span>
                      <span><i>03</i> Interview stories</span>
                    </div>
                  </section>
                )
              ) : (
                <section className="an-note-grid" aria-label="Notes">
                  {filteredNotes.map((note, index) => {
                    const excerpt = stripRichText(note.content);
                    return (
                      <article className="an-note-card" key={note._id} style={{ "--an-index": index }}>
                        <div className="an-note-card-topline">
                          <span>{String(index + 1).padStart(2, "0")}</span>
                          <span>{formatDate(note.updatedAt || note.createdAt)}</span>
                        </div>
                        <h2>{note.title}</h2>
                        <p>{excerpt || "Open this note to review its content."}</p>
                        <div className="an-note-card-footer">
                          <span>
                            <Paperclip size={14} />
                            {note.attachments?.length || 0} {note.attachments?.length === 1 ? "file" : "files"}
                          </span>
                          <div className="an-note-card-actions">
                            <IconButton label={`Edit ${note.title}`} onClick={() => startEditingNote(note)}><Edit size={16} /></IconButton>
                            <IconButton label={`Delete ${note.title}`} className="is-danger" onClick={() => handleDeleteNote(note)}><Trash2 size={16} /></IconButton>
                          </div>
                        </div>
                        <button type="button" className="an-open-note" onClick={() => setExpandedNoteId(note._id)}>
                          <span>Open note</span><ArrowRight size={17} />
                        </button>
                      </article>
                    );
                  })}
                </section>
              )}
            </div>
          )}
        </main>
      </div>

      {expandedNote && (
        <div className="an-reader-layer" role="presentation">
          <button className="an-reader-backdrop" aria-label="Close note" onClick={() => setExpandedNoteId(null)} />
          <section className="an-reader" role="dialog" aria-modal="true" aria-labelledby="an-reader-title">
            <header className="an-reader-header">
              <div>
                <span className="an-kicker">{currentPageData?.title}</span>
                <h2 id="an-reader-title">{expandedNote.title}</h2>
                <p><Calendar size={14} /> Updated {formatDate(expandedNote.updatedAt || expandedNote.createdAt)}</p>
              </div>
              <div>
                <ActionButton variant="secondary" icon={<Edit size={16} />} onClick={() => startEditingNote(expandedNote)}>Edit</ActionButton>
                <IconButton label="Close note" onClick={() => setExpandedNoteId(null)}><X size={20} /></IconButton>
              </div>
            </header>
            <div className="an-reader-body">
              <div
                className="an-rich-text"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(expandedNote.content || "") }}
              />
              {expandedNote.attachments?.length > 0 && (
                <section className="an-reader-attachments">
                  <div className="an-attachment-panel-title">
                    <div><Paperclip size={17} /><strong>Attachments</strong></div>
                    <span>{expandedNote.attachments.length}</span>
                  </div>
                  {expandedNote.attachments.map((file) => renderAttachment(file, expandedNote._id, true))}
                </section>
              )}
            </div>
            <footer className="an-reader-footer">
              <span>Saved in {currentPageData?.title}</span>
              <ActionButton variant="secondary" onClick={() => setExpandedNoteId(null)}>Close</ActionButton>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
};

export default NotesAurora;
